import { agencyContext } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useMemo } from 'react';
import { usePlans } from '@features/onboarding/hooks/use-plans';
import { agencyTypeToSegment } from '../components/agencyTypeToSegment';
import { useSeatUsage } from './useSeatUsage';
import { useSubscriptionDetail } from './useSubscriptionDetail';
import { useSubscriptionInvoices } from './useSubscriptionInvoices';
import { useUsageHistory } from './useUsageHistory';

const RECENT_INVOICES = 3;

/**
 * Everything the plan tab reads, fetched once.
 *
 * isLoading follows the subscription alone. Usage, history and invoices each
 * degrade to an empty state of their own, and letting any of them hold the
 * screen would cost the user their price and their cancel link over a bad
 * minute on a secondary endpoint.
 *
 * @param agencyId - The agency identifier.
 * @returns The tab's data and a refetch for the subscription.
 */
export function usePlanTabData(agencyId: string) {
  const agency = agencyContext.useStore((state) => state.agency);
  const { data: subscription, isLoading, isError, refetch } = useSubscriptionDetail(agencyId);
  const { data: usage, reload: reloadUsage } = useSeatUsage(agencyId);
  const { data: history } = useUsageHistory(agencyId);
  const { invoices, total } = useSubscriptionInvoices(agencyId, RECENT_INVOICES);
  const { plans, allPlansData } = usePlans({ defaultSegment: agencyTypeToSegment(agency.type) });

  // Every raw variant, both cycles: a yearly subscription's lines carry the
  // yearly variant's ids and prices, which the combined entries flatten away.
  const planVariants = useMemo(
    () => [...allPlansData.values()].flat(),
    [allPlansData],
  );

  return {
    subscription, usage, history, invoices, invoiceTotal: total, plans, planVariants,
    isLoading, isError, refetch, reloadUsage,
  };
}
